/** Convert a PT-BR money string ("1.234,56") to integer cents (123456). */
export const ptbrToCents = (value?: string): number => {
  if (!value) return 0;
  const n = Number(value.replace(/\./g, '').replace(',', '.'));
  return Number.isFinite(n) ? Math.round(n * 100) : 0;
};

/** Convert integer cents (123456) to a PT-BR money string ("1.234,56"). */
export const centsToPtbr = (cents?: number): string => {
  if (cents === undefined || cents === null || !Number.isFinite(cents)) return '0,00';
  return (cents / 100).toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

/** Format integer cents as "R$ 1.234,56" or "-" for null/undefined. */
export function formatCents(cents: number | undefined | null): string {
  if (cents === undefined || cents === null) return '-';
  return `R$ ${centsToPtbr(cents)}`;
}

/** Format a data amount in MB as "XGB" or "YMB". */
export function formatDataAmount(mb: number): string {
  return mb >= 1000 ? `${mb / 1000}GB` : `${mb}MB`;
}

export function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

export function formatDateTime(dateString: string): string {
  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).format(new Date(dateString));
}

export function maskCpfCnpj(value: string): string {
  // Remove todos os caracteres nao numericos
  const digits = value.replace(/\D/g, '');

  // Limita a 14 digitos (tamanho maximo de um CNPJ)
  const limitedDigits = digits.slice(0, 14);

  // Verifica se e CPF ou CNPJ baseado no numero de digitos
  if (limitedDigits.length <= 11) {
    // Formata como CPF: 123.456.789-01
    let result = limitedDigits;

    if (limitedDigits.length > 3) {
      result = `${limitedDigits.substring(0, 3)}.${limitedDigits.substring(3)}`;
    }

    if (limitedDigits.length > 6) {
      result = `${result.substring(0, 7)}.${result.substring(7)}`;
    }

    if (limitedDigits.length > 9) {
      result = `${result.substring(0, 11)}-${result.substring(11)}`;
    }

    return result;
  } else {
    // Formata como CNPJ: 12.345.678/0001-99
    let result = limitedDigits;

    if (limitedDigits.length > 2) {
      result = `${limitedDigits.substring(0, 2)}.${limitedDigits.substring(2)}`;
    }

    if (limitedDigits.length > 5) {
      result = `${result.substring(0, 6)}.${result.substring(6)}`;
    }

    if (limitedDigits.length > 8) {
      result = `${result.substring(0, 10)}/${result.substring(10)}`;
    }

    if (limitedDigits.length > 12) {
      result = `${result.substring(0, 15)}-${result.substring(15)}`;
    }

    return result;
  }
}

export function isValidCPF(cpf: string): boolean {
  cpf = cpf.replace(/[^\d]+/g, '');

  if (cpf.length !== 11 || /^(\d)\1+$/.test(cpf)) {
    return false;
  }

  let sum = 0;
  for (let i = 0; i < 9; i++) {
    sum += parseInt(cpf.charAt(i), 10) * (10 - i);
  }
  let rev = 11 - (sum % 11);
  if (rev === 10 || rev === 11) rev = 0;
  if (rev !== parseInt(cpf.charAt(9), 10)) return false;

  sum = 0;
  for (let i = 0; i < 10; i++) {
    sum += parseInt(cpf.charAt(i), 10) * (11 - i);
  }
  rev = 11 - (sum % 11);
  if (rev === 10 || rev === 11) rev = 0;
  return rev === parseInt(cpf.charAt(10), 10);
}

export function isValidCNPJ(cnpj: string): boolean {
  // Remover caracteres nao numericos
  cnpj = cnpj.replace(/[^\d]+/g, '');

  // Verificar se o CNPJ possui 14 caracteres
  if (cnpj.length !== 14) {
    return false;
  }

  // Validar CNPJs conhecidos como invalidos
  if (
    /^(00000000000000|11111111111111|22222222222222|33333333333333|44444444444444|55555555555555|66666666666666|77777777777777|88888888888888|99999999999999)$/.test(
      cnpj,
    )
  ) {
    return false;
  }

  // Validar o primeiro digito verificador
  let soma = 0;
  let peso = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
  for (let i = 0; i < 12; i++) {
    soma += parseInt(cnpj[i], 10) * peso[i];
  }
  let resto = soma % 11;
  const digito1 = resto < 2 ? 0 : 11 - resto;

  // Validar o segundo digito verificador
  soma = 0;
  peso = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
  for (let i = 0; i < 13; i++) {
    soma += parseInt(cnpj[i], 10) * peso[i];
  }
  resto = soma % 11;
  const digito2 = resto < 2 ? 0 : 11 - resto;

  // Verificar se os digitos verificadores sao iguais aos fornecidos
  if (digito1 === parseInt(cnpj[12], 10) && digito2 === parseInt(cnpj[13], 10)) {
    return true;
  }
  return false;
}

// Alias para consistência com código existente
export const formatCpfCnpj = maskCpfCnpj;

/**
 * Mask CPF only: 000.000.000-00 (limited to 11 digits)
 * Use this for fields that only accept CPF, not CNPJ.
 */
export function maskCpf(value: string): string {
  const digits = value.replace(/\D/g, '').slice(0, 11);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `${digits.slice(0, 3)}.${digits.slice(3)}`;
  if (digits.length <= 9) return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6)}`;
  return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6, 9)}-${digits.slice(9)}`;
}

/**
 * Mask RG: XX.XXX.XXX-X
 * Accepts digits and letters (check digit can be a letter).
 */
export function maskRg(value: string): string {
  const chars = value
    .replace(/[^a-zA-Z0-9]/g, '')
    .toUpperCase()
    .slice(0, 10);
  if (chars.length <= 2) return chars;
  if (chars.length <= 5) return `${chars.slice(0, 2)}.${chars.slice(2)}`;
  if (chars.length <= 8) return `${chars.slice(0, 2)}.${chars.slice(2, 5)}.${chars.slice(5)}`;
  return `${chars.slice(0, 2)}.${chars.slice(2, 5)}.${chars.slice(5, 8)}-${chars.slice(8)}`;
}
